import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  Edit2,
  Eye,
  Save,
  Target,
  X,
  Lock,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";

const SistemaEducacional = () => {
  const [abaAtiva, setAbaAtiva] = useState("lancamento");
  const [expandedRows, setExpandedRows] = useState({});
  const [expandedCourses, setExpandedCourses] = useState({});
  const [movimentosData, setMovimentosData] = useState([]);
  const [loadingMovimentos, setLoadingMovimentos] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState("");
  const ano = localStorage.getItem("selectedYear");
  const anoAtual = new Date().getFullYear();

  console.log("Ano Geral" + ano)

  // ✅ FUNÇÕES DE FORMATAÇÃO
  const formatInteger = (value) => {
    if (value === null || value === undefined || value === '') return '0';
    return parseInt(value).toLocaleString('pt-BR');
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(parseFloat(value));
  };

  const formatDecimal = (value, decimals = 2) => {
    if (value === null || value === undefined || value === '') return '0,00';
    return parseFloat(value).toLocaleString('pt-BR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };

  // Função para determinar o tipo de formatação baseado no campo
  const getFieldType = (field) => {
    const integerFields = ['matricula', 'evasao', 'qntbolsaprotocolo', 'qntceb100', 'qntceb50', 'div'];
    const currencyFields = ['vlranuidade', 'vlrbolsadiv'];
    
    //const fieldLower = field.toLowerCase().replace(/[^a-z]/g, '');
    const fieldLower = field.toLowerCase();
    
    if (integerFields.includes(fieldLower)) return 'integer';
    if (currencyFields.includes(fieldLower)) return 'currency';
    return 'decimal';
  };

  const formatValue = (value, field) => {
    const fieldType = getFieldType(field);
    
    switch (fieldType) {
      case 'integer':
        return formatInteger(value);
      case 'currency':
        return formatCurrency(value);
      case 'decimal':
      default:
        return formatDecimal(value);
    }
  };

  const parseFormattedValue = (formattedValue, field) => {
    if (!formattedValue) return 0;
    
    const cleanValue = formattedValue
      .toString()
      .replace(/R\$\s?/g, '')
      .replace(/\./g, '')
      .replace(',', '.');
    
    const fieldType = getFieldType(field);
    
    if (fieldType === 'integer') {
      return parseInt(cleanValue) || 0;
    }
    
    return parseFloat(cleanValue) || 0;
  };

  // ✅ VERIFICAÇÃO SE PODE EDITAR
  const podeEditar = () => {
    const anoSelecionado = parseInt(ano);
    return anoSelecionado >= localStorage.getItem("selectedTrava"); //anoAtual;
  };

  const carregarMovimentos = useCallback(async () => {
    setLoadingMovimentos(true);

    try {
      // Tentar carregar dados da API primeiro
      await carregarDadosAPI();
    } catch (error) {
      console.log("Erro na API, usando dados de exemplo:", error);
      // Fallback para dados de exemplo
      await new Promise((resolve) => setTimeout(resolve, 800));
      //setMovimentosData(dadosExemplo);
    }

    setLoadingMovimentos(false);
  }, [ano]);

  // Função para carregar dados da API
  const carregarDadosAPI = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      throw new Error("Token não encontrado");
    }

    const PCODCOLIGADA = localStorage.getItem("PCODCOLIGADA") || "1";
    const PCODFILIAL = localStorage.getItem("PCODFILIAL") || "2";

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    // Função para determinar segmento (baseada nos dados reais do CSV)
    const determinarSegmento = (curso, serie) => {
      if (!curso && !serie) return "Outros";

      const cursoLower = String(curso).toLowerCase();
      const serieLower = String(serie).toLowerCase();

      // Educação Infantil
      if (
        cursoLower.includes("educação infantil") ||
        cursoLower.includes("infantil") ||
        serieLower.includes("infantil") ||
        serieLower.includes("maternal") ||
        serieLower.includes("g5") ||
        serieLower.includes("g6")
      ) {
        return "Infantil";
      }

      // Ensino Fundamental 1 (1º ao 5º ano)
      if (
        cursoLower.includes("ensino fundamental 1") ||
        (cursoLower.includes("fundamental") && parseInt(serie) <= 15)
      ) {
        return "Fundamental I";
      }

      // Ensino Fundamental 2 (6º ao 9º ano)
      if (
        cursoLower.includes("ensino fundamental 2") ||
        cursoLower.includes("fundamental eja") ||
        (cursoLower.includes("fundamental") &&
          parseInt(serie) >= 11 &&
          parseInt(serie) <= 14)
      ) {
        return "Fundamental II";
      }

      // Ensino Médio
      if (
        cursoLower.includes("ensino médio") ||
        cursoLower.includes("médio eja") ||
        (parseInt(serie) >= 1 &&
          parseInt(serie) <= 3 &&
          cursoLower.includes("médio"))
      ) {
        return "Médio";
      }

      // Técnico
      if (
        cursoLower.includes("técnico") ||
        cursoLower.includes("administração") ||
        cursoLower.includes("logística") ||
        cursoLower.includes("qualidade") ||
        cursoLower.includes("informática")
      ) {
        return "Técnico";
      }

      return "Outros";
    };

    // Função para determinar grupo (baseada nos dados reais do CSV)
    const determinarGrupo = (curso, serie) => {
      if (!curso && !serie) return "Outros";

      const cursoLower = String(curso).toLowerCase();
      const serieLower = String(serie).toLowerCase();

      // Educação Infantil
      if (
        cursoLower.includes("educação infantil") ||
        serieLower.includes("infantil") ||
        serieLower.includes("maternal")
      ) {
        return "Educação Infantil";
      }

      // Ensino Fundamental 1
      if (cursoLower.includes("ensino fundamental 1")) {
        return "Ensino Fundamental 1";
      }

      // Ensino Fundamental 2
      if (cursoLower.includes("ensino fundamental 2")) {
        return "Ensino Fundamental 2";
      }

      // EJA Fundamental
      if (cursoLower.includes("fundamental eja")) {
        return "EJA Fundamental";
      }

      // Ensino Médio
      if (cursoLower.includes("ensino médio") && !cursoLower.includes("eja")) {
        return "Ensino Médio";
      }

      // EJA Médio
      if (cursoLower.includes("médio eja")) {
        return "EJA Médio";
      }

      // Técnicos específicos
      if (cursoLower.includes("administração"))
        return "Técnico em Administração";
      if (cursoLower.includes("logística")) return "Técnico em Logística";
      if (cursoLower.includes("qualidade")) return "Técnico em Qualidade";
      if (cursoLower.includes("informática")) return "Técnico Informática";

      return "Outros";
    };

    let whereClause = `AND CODCOLIGADA = '${PCODCOLIGADA}' AND CODFILIAL = '${PCODFILIAL}' AND ANO = '${localStorage.getItem("selectedYear")}'`;
    //let whereClause = `AND CODCOLIGADA = '${PCODCOLIGADA}' AND CODFILIAL = '${PCODFILIAL}' AND ANO = '2025'`;

    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/totvs-sql/sql_Man_ORC_0015/filter`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({
          WHERE: whereClause,
          ORDER_BY: "CAST(CODCURSO AS INT), CAST(CODSERIE AS INT)",
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (result.success && result.data && result.data.length > 0) {
      const movimentosMapeados = result.data.map((item) => ({
        // Identificadores
        iderecdu: item.IDERECDU,
        codcoligada: parseInt(item.CODCOLIGADA) || 0,
        codfilial: parseInt(item.CODFILIAL) || 0,
        ano: parseInt(item.ANO) || ano,
        codcurso: item.CODCURSO || "",
        codserie: item.CODSERIE || "",
        serie: item.SERIE || "",

        // Campos editáveis principais
        matricula: parseInt(item.MATRICULA) || 0,
        qntbolsaprotocolo: parseInt(item.QNTBOLSAPROTOCOLO) || 0,
        qntceb100: parseInt(item.QNTCEB100) || 0,
        qntceb50: parseInt(item.QNTCEB50) || 0,
        evasao: parseInt(item.EVASAO) || 0,
        vlranuidade: parseFloat(item.VLRANUIDADE) || 0,
        vlrbolsadiv: parseFloat(item.VLRBOLSADIV) || 0,

        // Campos calculados/visualização
        qntpagantes: parseInt(item.QNTPAGANTES) || 0,
        qntcebdiv: parseInt(item.QNTCEBDIV) || 0,
        vlrreceitabruta: parseFloat(item.VLRRECEITABRUTA) || 0,
        vlrbolsaprotocolo: parseFloat(item.VLRBOLSAPROTOCOLO) || 0,
        vlrceb100: parseInt(item.VLRCEB100) || 0,
        vlrceb50: parseInt(item.VLRCEB50) || 0,
        vlrreceitaliquida: parseFloat(item.VLRRECEITALIQUIDA) || 0,

        // Campos auxiliares
        tipo: item.TIPO || "U",
        codccusto: item.CODCCUSTO || "",
        qntsaldo: parseInt(item.QNTSALDO) || 0,
        cebpagas: parseInt(item.CEBPAGAS) || 0,
        vlrsaldo: parseFloat(item.VLRSALDO) || 0,
        div: parseFloat(item.DIV) || 0,

        // Campos derivados para interface (usando campos CURSO e SERIE do CSV)
        segmento: determinarSegmento(item.CURSO || "", item.SERIE || ""),
        grupo: determinarGrupo(item.CURSO || "", item.SERIE || ""),
        curso: item.SERIE || item.CODSERIE || "N/A",
        turma: item.TURMA || "A",
        realizado: parseFloat(item.VALORORIGINAL) || 0,
        orcado: parseFloat(item.VLRRECEITALIQUIDA) || 0,
        vlrmulta: parseFloat(item.VALORMULTA) || 0,
        vlrjuros: parseFloat(item.VALORJUROS) || 0,
        vlrdesconto: parseFloat(item.VALORDESCONTO) || 0,
        vlrliquido: parseFloat(item.VALORLIQUIDO) || 0,
      }));

      setMovimentosData(movimentosMapeados);
      console.log(
        "✅ Dados carregados da API:",
        movimentosMapeados.length,
        "registros"
      );
    } else {
      throw new Error("Nenhum dado retornado da API");
    }
  };

  useEffect(() => {
    carregarMovimentos();
  }, [carregarMovimentos]);

  // ✅ FUNÇÃO PARA ATUALIZAR CAMPOS DO ORÇAMENTO
  const atualizarCampoOrcamento = useCallback(
    async (iderecdu, campo, valor) => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          return { success: false, message: "Token não encontrado" };
        }

        const tokenPayload = JSON.parse(atob(token.split(".")[1]));

        const headers = {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        };

        const dados = {
          Id: iderecdu,
          Campo: campo.toUpperCase(), // Converter para maiúsculo para coincidir com BD
          valor: valor,
          dataAlteracao: new Date().toISOString(),
          usuario: tokenPayload.username || tokenPayload.id,
          codfilial: localStorage.getItem("PCODFILIAL"),
        };

        console.log("💾 Salvando campo do orçamento:", dados);

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/SQlCustom/updateEdu`,
          {
            method: "POST",
            headers,
            body: JSON.stringify(dados),
          }
        );

        const result = await response.json();

        if (response.ok) {
          console.log("✅ Campo do orçamento atualizado com sucesso");
          carregarDadosAPI();
          return { success: true, data: result };
        } else {
          console.error("❌ Erro ao atualizar campo do orçamento:", result);
          return {
            success: false,
            message: result.error || "Erro desconhecido",
          };
        }
      } catch (error) {
        console.error("❌ Erro na requisição de atualização:", error);
        return { success: false, message: error.message };
      }
    },
    []
  );

  const toggleRowExpansion = (iderecdu) => {
    setExpandedRows((prev) => ({
      ...prev,
      [iderecdu]: !prev[iderecdu],
    }));
  };

  const toggleCourseExpansion = (curso) => {
    setExpandedCourses((prev) => ({
      ...prev,
      [curso]: !prev[curso],
    }));
  };

  const startEditing = (iderecdu, field, currentValue) => {
    // Verificar se pode editar antes de iniciar
    if (!podeEditar()) {
      // Mostrar toast de aviso
      const toast = document.createElement("div");
      toast.className = "position-fixed top-0 end-0 p-3";
      toast.style.zIndex = "9999";
      toast.innerHTML = `
        <div class="toast show" role="alert">
          <div class="toast-header bg-warning text-dark">
            <strong class="me-auto">🔒 Edição Bloqueada</strong>
            <button type="button" class="btn-close" onclick="this.closest('.toast').remove()"></button>
          </div>
          <div class="toast-body">
            Não é possível editar dados do ano ${ano}. Apenas anos futuros (>${anoAtual}) podem ser editados.
          </div>
        </div>
      `;
      document.body.appendChild(toast);

      setTimeout(() => {
        if (toast.parentNode) {
          toast.remove();
        }
      }, 4000);
      return;
    }

    const rawValue = parseFormattedValue(currentValue, field);
    setEditingField(`${iderecdu}-${field}`);
    setEditValue(rawValue.toString());
  };

  const saveEdit = async (iderecdu, field) => {
    const fieldType = getFieldType(field);
    let numericValue;
    
    if (fieldType === 'integer') {
      numericValue = parseInt(editValue);
    } else {
      numericValue = parseFloat(editValue);
    }
    
    if (!isNaN(numericValue)) {
      // Primeiro atualizar localmente para feedback imediato
      setMovimentosData((prev) =>
        prev.map((item) =>
          item.iderecdu === iderecdu ? { ...item, [field]: numericValue } : item
        )
      );

      setEditingField(null);
      setEditValue("");

      // Tentar salvar no banco de dados
      try {
        const resultado = await atualizarCampoOrcamento(
          iderecdu,
          field,
          numericValue
        );

        if (resultado.success) {
          // Mostrar toast de sucesso
          const toast = document.createElement("div");
          toast.className = "position-fixed top-0 end-0 p-3";
          toast.style.zIndex = "9999";
          toast.innerHTML = `
            <div class="toast show" role="alert">
              <div class="toast-header bg-success text-white">
                <strong class="me-auto">✅ Sucesso</strong>
                <button type="button" class="btn-close btn-close-white" onclick="this.closest('.toast').remove()"></button>
              </div>
              <div class="toast-body">
                Campo ${field} atualizado com sucesso!
              </div>
            </div>
          `;
          document.body.appendChild(toast);

          setTimeout(() => {
            if (toast.parentNode) {
              toast.remove();
            }
          }, 3000);
        } else {
          throw new Error(resultado.message);
        }
      } catch (error) {
        console.error("❌ Erro ao salvar no banco:", error);

        // Mostrar toast de erro
        const toast = document.createElement("div");
        toast.className = "position-fixed top-0 end-0 p-3";
        toast.style.zIndex = "9999";
        toast.innerHTML = `
          <div class="toast show" role="alert">
            <div class="toast-header bg-danger text-white">
              <strong class="me-auto">❌ Erro</strong>
              <button type="button" class="btn-close btn-close-white" onclick="this.closest('.toast').remove()"></button>
            </div>
            <div class="toast-body">
              Erro ao salvar: ${error.message}
            </div>
          </div>
        `;
        document.body.appendChild(toast);

        setTimeout(() => {
          if (toast.parentNode) {
            toast.remove();
          }
        }, 4000);
      }
    }
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditValue("");
  };

  // ✅ COMPONENTE PARA CAMPO EDITÁVEL COM FORMATAÇÃO E BLOQUEIO
  const EditableField = ({
    value,
    iderecdu,
    field,
    prefix = "",
    suffix = "",
    className = "",
  }) => {
    const fieldKey = `${iderecdu}-${field}`;
    const isEditing = editingField === fieldKey;
    const editavel = podeEditar();
    const fieldType = getFieldType(field);

    // Função para determinar o step do input
    const getInputStep = () => {
      return fieldType === 'integer' ? '1' : '0.01';
    };

    if (isEditing && editavel) {
      return (
        <div className="d-flex align-items-center gap-2">
          <input
            type="number"
            step={getInputStep()}
            className="form-control form-control-sm"
            style={{ width: "120px" }}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") saveEdit(iderecdu, field);
              if (e.key === "Escape") cancelEdit();
            }}
            onBlur={() => saveEdit(iderecdu, field)}
            autoFocus
          />
          <button
            className="btn btn-success btn-sm"
            onClick={() => saveEdit(iderecdu, field)}
          >
            <Save size={14} />
          </button>
          <button className="btn btn-secondary btn-sm" onClick={cancelEdit}>
            <X size={14} />
          </button>
        </div>
      );
    }

    return (
      <span
        className={`editable-field ${className} ${!editavel ? 'editable-field-disabled' : ''}`}
        onClick={() => startEditing(iderecdu, field, value)}
        title={!editavel ? `Edição bloqueada para ${ano}. Apenas anos futuros podem ser editados.` : "Clique para editar"}
      >
        {prefix}
        {formatValue(value, field)}
        {suffix}
        {editavel ? (
          <Edit2 size={12} className="edit-icon ms-1" />
        ) : (
          <Lock size={12} className="lock-icon ms-1 text-muted" />
        )}
      </span>
    );
  };

  // Aba Lançamento
  const AbaLancamento = () => (
    <div className="container-fluid">
      {!podeEditar() && (
        <div className="row mb-3">
          <div className="col">
            <div className="alert alert-warning d-flex align-items-center">
              <Lock size={20} className="me-2" />
              <div>
                <strong>Modo Somente Leitura</strong>
                <div className="small">
                  Os valores orçamentários não podem ser editados para o ano {ano}. 
                  Apenas anos futuros ({anoAtual + 1} em diante) permitem alterações.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="row mb-4">
        <div className="col">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom">
              <h5 className="mb-0 text-dark fw-semibold">
                Lançamentos Orçamentários
              </h5>
              <small className="text-muted">
                {podeEditar() 
                  ? "Edite os valores clicando nos campos destacados"
                  : `Visualização dos dados de ${ano} (somente leitura)`
                }
              </small>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="fw-semibold text-muted">Curso/Turma</th>
                      <th className="fw-semibold text-muted">Matrículas</th>
                      <th className="fw-semibold text-muted">Evasão</th>
                      <th className="fw-semibold text-muted">B.Diversas(%)</th>
                      <th className="fw-semibold text-muted">B. Prot.</th>
                      <th className="fw-semibold text-muted">Ceb.100%</th>
                      <th className="fw-semibold text-muted">Ceb.50%</th>
                      <th className="fw-semibold text-muted">Valor Anuidade</th>
                      <th className="fw-semibold text-muted">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movimentosData.map((item) => (
                      <React.Fragment key={item.iderecdu}>
                        <tr>
                          <td>
                            <div>
                              <div className="fw-semibold text-dark">
                                {item.curso}
                              </div>
                              <small className="text-muted">
                                {item.grupo}
                              </small>
                            </div>
                          </td>
                          <td>
                            <EditableField
                              value={item.matricula}
                              iderecdu={item.iderecdu}
                              field="matricula"
                              className="text-primary fw-bold"
                            />
                          </td>

                          <td>
                            <EditableField
                              value={item.evasao}
                              iderecdu={item.iderecdu}
                              field="evasao"
                              className="text-secondary fw-bold"
                            />
                          </td>

                          <td>
                            <EditableField
                              value={item.div}
                              iderecdu={item.iderecdu}
                              field="div"
                              className="text-secondary fw-bold"
                            />
                          </td>

                          <td>
                            <EditableField
                              value={item.qntbolsaprotocolo}
                              iderecdu={item.iderecdu}
                              field="qntbolsaprotocolo"
                              className="text-secondary fw-bold"
                            />
                          </td>

                          <td>
                            <EditableField
                              value={item.qntceb100 }
                              iderecdu={item.iderecdu}
                              field="qntceb100"
                              className="text-secondary fw-bold"
                            />
                          </td>

                          <td>
                            <EditableField
                              value={item.qntceb50}
                              iderecdu={item.iderecdu}
                              field="qntceb50"
                              className="text-secondary fw-bold"
                            />
                          </td>

                          <td>
                            <EditableField
                              value={item.vlranuidade}
                              iderecdu={item.iderecdu}
                              field="vlranuidade"
                              className="text-success fw-bold"
                            />
                          </td>
                          <td>
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => toggleRowExpansion(item.iderecdu)}
                            >
                              {expandedRows[item.iderecdu] ? (
                                <ChevronDown size={16} />
                              ) : (
                                <ChevronRight size={16} />
                              )}
                              Detalhes
                            </button>
                          </td>
                        </tr>
                        {expandedRows[item.iderecdu] && (
                          <tr>
                            <td colSpan="9" className="bg-light">
                              <div className="p-4">
                                <div className="row mb-3">
                                  <div className="col">
                                    <h6 className="text-dark mb-0">
                                      Dados Orçamentários - {item.curso}
                                    </h6>
                                    <small className="text-muted">
                                      Informações calculadas do orçamento
                                    </small>
                                  </div>
                                </div>

                                {/* Primeira linha - Quantidades */}
                                <div className="row g-3 mb-3">
                                  <div className="col-md-2">
                                    <div className="card border-0 bg-white">
                                      <div className="card-body text-center">
                                        <div className="text-muted small">
                                          Quantidade Pagantes
                                        </div>
                                        <div className="h5 mb-0 text-success fw-bold">
                                          {formatInteger(item.qntpagantes)}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-md-2">
                                    <div className="card border-0 bg-white">
                                      <div className="card-body text-center">
                                        <div className="text-muted small">
                                          Quantidade CEB Devido
                                        </div>
                                        <div className="h5 mb-0 text-info fw-bold">
                                          {formatInteger(item.qntcebdiv)}
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="col-md-2">
                                    <div className="card border-0 bg-white">
                                      <div className="card-body text-center">
                                        <div className="text-muted small">
                                          Bolsa Diversas
                                        </div>
                                        <div className="h5 mb-0 text-warning fw-semibold">
                                          {formatCurrency(item.vlrbolsadiv)}
                                        </div>
                                      </div>
                                    </div>
                                  </div>


                                  <div className="col-md-2">
                                    <div className="card border-0 bg-white">
                                      <div className="card-body text-center">
                                        <div className="text-muted small">
                                          Bolsa Protocolo
                                        </div>
                                        <div className="h5 mb-0 text-warning fw-semibold">
                                          {formatCurrency(item.vlrbolsaprotocolo)}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-md-2">
                                    <div className="card border-0 bg-white">
                                      <div className="card-body text-center">
                                        <div className="text-muted small">
                                          CEB 100% (Valor)
                                        </div>
                                        <div className="h5 mb-0 text-success fw-semibold">
                                          {formatCurrency(item.vlrceb100)}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-md-2">
                                    <div className="card border-0 bg-white">
                                      <div className="card-body text-center">
                                        <div className="text-muted small">
                                          CEB 50% (Valor)
                                        </div>
                                        <div className="h5 mb-0 text-info fw-semibold">
                                          {formatCurrency(item.vlrceb50)}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/*
                                  <div className="col-md-2">
                                    <div className="card border-0 bg-white">
                                      <div className="card-body text-center">
                                        <div className="text-muted small">
                                          Evasão (Valor)
                                        </div>
                                        <div className="h5 mb-0 text-danger fw-semibold">
                                          {formatCurrency(item.evasao)}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  */}

                                </div>
                                
                                <div className="row g-3">
                                  <div className="col-md-6">
                                    <div className="card border-0 bg-primary bg-opacity-10">
                                      <div className="card-body text-center">
                                        <div className="text-muted small">
                                          Receita Bruta
                                        </div>
                                        <div className="h4 mb-0 text-primary fw-bold">
                                          {formatCurrency(item.vlrreceitabruta)}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-md-6">
                                    <div className="card border-0 bg-success bg-opacity-10">
                                      <div className="card-body text-center">
                                        <div className="text-muted small">
                                          Receita Líquida
                                        </div>
                                        <div className="h4 mb-0 text-success fw-bold">
                                          {formatCurrency(item.vlrreceitaliquida)}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Aba Conferência
  const AbaConferencia = () => {
    const dadosAgrupados = movimentosData.reduce((acc, item) => {
      const key = item.curso;
      if (!acc[key]) {
        acc[key] = {
          curso: item.curso,
          grupo: item.grupo,
          bruto: 0,
          totalDesconto: 0,
          liquida: 0,
          matriculas: 0,
        };
      }
      acc[key].bruto += item.vlrreceitabruta;
      acc[key].totalDesconto +=
        item.vlrbolsadiv +
        item.vlrbolsaprotocolo +
        item.vlrceb100 +
        item.vlrceb50;
      acc[key].liquida += item.vlrreceitaliquida;
      acc[key].matriculas += item.matricula;
      return acc;
    }, {});

    return (
      <div className="container-fluid">
        <div className="row g-4">
          {Object.values(dadosAgrupados).map((item, index) => (
            <div key={index} className="col-lg-4 col-md-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header bg-white border-bottom-0">
                  <h6 className="mb-1 fw-semibold text-dark">{item.curso}</h6>
                  <small className="text-muted">
                    {item.grupo} • {formatInteger(item.matriculas)} alunos
                  </small>
                </div>
                <div className="card-body">
                  <div className="row text-center">
                    <div className="col-4">
                      <div className="border-end">
                        <div className="text-muted small mb-1">Bruto</div>
                        <div className="fw-semibold text-primary">
                          {formatCurrency(item.bruto)}
                        </div>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="border-end">
                        <div className="text-muted small mb-1">Desconto</div>
                        <div className="fw-semibold text-danger">
                          {formatCurrency(item.totalDesconto)}
                        </div>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="text-muted small mb-1">Líquida</div>
                      <div className="fw-semibold text-success">
                        {formatCurrency(item.liquida)}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="progress" style={{ height: "6px" }}>
                      <div
                        className="progress-bar bg-success"
                        style={{
                          width: `${(item.liquida / item.bruto) * 100}%`,
                        }}
                      ></div>
                    </div>
                    <small className="text-muted">
                      Liquído: {((item.liquida / item.bruto) * 100).toFixed(2)}%
                    </small>
                    <small className="text-muted">
                      {" "}
                      Desconto:{" "}
                      {(100 - (item.liquida / item.bruto) * 100).toFixed(2)}%
                    </small>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Aba Realizado
  const AbaRealizado = () => {
    // Consolidar dados por curso
    const dadosConsolidados = movimentosData.reduce((acc, item) => {
      if (item.orcado <= 0) {
        return acc;
      }

      const cursoPrincipal = item.grupo; // Usar grupo como curso principal

      if (!acc[cursoPrincipal]) {
        acc[cursoPrincipal] = {
          curso: cursoPrincipal,
          totalOrcado: 0,
          totalRealizado: 0,
          totalMulta: 0,
          totalJuros: 0,
          totalLiquido: 0,
          totaldesconto: 0,
          series: {},
        };
      }

      // Consolidar por série dentro do curso
      const serie = item.serie;
      if (!acc[cursoPrincipal].series[serie]) {
        acc[cursoPrincipal].series[serie] = {
          serie: serie,
          totalOrcado: 0,
          totalRealizado: 0,
          totalMulta: 0,
          totalJuros: 0,
          totalLiquido: 0,
          totaldesconto: 0,
          turmas: [],
        };
      }

      // Adicionar dados da turma
      acc[cursoPrincipal].series[serie].turmas.push({
        ...item,
        liquidoFinal:
          item.realizado + (item.vlrmulta || 0) + (item.vlrjuros || 0),
      });

      // Somar aos totais da série
      acc[cursoPrincipal].series[serie].totalOrcado += item.orcado;
      acc[cursoPrincipal].series[serie].totalRealizado += item.realizado;
      acc[cursoPrincipal].series[serie].totalMulta += item.vlrmulta || 0;
      acc[cursoPrincipal].series[serie].totalJuros += item.vlrjuros || 0;
      acc[cursoPrincipal].series[serie].totaldesconto += item.vlrdesconto || 0;
      acc[cursoPrincipal].series[serie].totalLiquido += item.vlrliquido || 0;

      // Somar aos totais do curso
      acc[cursoPrincipal].totalOrcado += item.orcado;
      acc[cursoPrincipal].totalRealizado += item.realizado;
      acc[cursoPrincipal].totalMulta += item.vlrmulta || 0;
      acc[cursoPrincipal].totalJuros += item.vlrjuros || 0;
      acc[cursoPrincipal].totalLiquido += item.vlrliquido || 0;
      acc[cursoPrincipal].totaldesconto += item.vlrdesconto || 0;

      return acc;
    }, {});

    return (
      <div className="container-fluid">
        <div className="row mb-4">
          <div className="col">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-bottom">
                <h5 className="mb-0 text-dark fw-semibold">
                  Orçamento vs Realizado - Visão Hierárquica
                </h5>
                <small className="text-muted">
                  Consolidado por Curso → Série
                </small>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="fw-semibold text-muted">
                          Curso/Série
                        </th>
                        <th className="fw-semibold text-muted">Valor Orçado</th>
                        <th className="fw-semibold text-muted">
                          Valor Realizado
                        </th>
                        <th className="fw-semibold text-muted">Multa</th>
                        <th className="fw-semibold text-muted">Juros</th>
                        <th className="fw-semibold text-muted">Desconto</th>
                        <th className="fw-semibold text-muted">
                          Líquido Final
                        </th>
                        <th className="fw-semibold text-muted">Performance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.values(dadosConsolidados).map((curso) => {
                        const performanceCurso =
                          (curso.totalRealizado / curso.totalOrcado) * 100;

                        return (
                          <React.Fragment key={curso.curso}>
                            {/* Linha do Curso */}
                            <tr className="table-primary">
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <button
                                    className="btn btn-sm btn-link p-0"
                                    onClick={() =>
                                      toggleCourseExpansion(curso.curso)
                                    }
                                  >
                                    {expandedCourses[curso.curso] ? (
                                      <ChevronDown size={16} />
                                    ) : (
                                      <ChevronRight size={16} />
                                    )}
                                  </button>
                                  <div>
                                    <span className="fw-bold text-primary">
                                      {curso.curso}
                                    </span>
                                    <div className="small text-muted">
                                      {Object.keys(curso.series).length} séries
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <span className="fw-bold text-primary">
                                  {formatCurrency(curso.totalOrcado)}
                                </span>
                              </td>
                              <td>
                                <span className="fw-bold text-success">
                                  {formatCurrency(curso.totalRealizado)}
                                </span>
                              </td>
                              <td>
                                <span className="text-primary fw-semibold">
                                  {formatCurrency(curso.totalMulta)}
                                </span>
                              </td>
                              <td>
                                <span className="text-primary fw-semibold">
                                  {formatCurrency(curso.totalJuros)}
                                </span>
                              </td>
                              <td>
                                <span className="text-danger fw-semibold">
                                  {formatCurrency(curso.totaldesconto)}
                                </span>
                              </td>
                              <td>
                                <span className="fw-bold text-dark">
                                  {formatCurrency(curso.totalLiquido)}
                                </span>
                              </td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <div
                                    className="progress flex-grow-1"
                                    style={{ height: "8px", width: "60px" }}
                                  >
                                    <div
                                      className={`progress-bar ${
                                        performanceCurso >= 90
                                          ? "bg-success"
                                          : performanceCurso >= 50
                                          ? "bg-primary"
                                          : "bg-danger"
                                      }`}
                                      style={{
                                        width: `${Math.min(
                                          performanceCurso,
                                          100
                                        )}%`,
                                      }}
                                    ></div>
                                  </div>
                                  <span
                                    className={`small fw-bold ${
                                      performanceCurso >= 90
                                        ? "text-success"
                                        : performanceCurso >= 50
                                        ? "text-primary"
                                        : "text-danger"
                                    }`}
                                  >
                                    {performanceCurso.toFixed(1)}%
                                  </span>
                                </div>
                              </td>
                            </tr>

                            {/* Séries do Curso */}
                            {expandedCourses[curso.curso] &&
                              Object.values(curso.series).map((serie) => {
                                const performanceSerie =
                                  (serie.totalRealizado / serie.totalOrcado) *
                                  100;
                                const serieKey = `${curso.curso}-${serie.serie}`;

                                return (
                                  <React.Fragment key={serieKey}>
                                    {/* Linha da Série */}
                                    <tr className="table-secondary">
                                      <td>
                                        <div
                                          className="d-flex align-items-center gap-2"
                                          style={{ paddingLeft: "30px" }}
                                        >
                                          {/*<button
                                            className="btn btn-sm btn-link p-0"
                                            onClick={() =>
                                              toggleSeriesExpansion(serieKey)
                                            }
                                          >
                                            {expandedSeries[serieKey] ? (
                                              <ChevronDown size={14} />
                                            ) : (
                                              <ChevronRight size={14} />
                                            )}
                                          </button>*/}
                                          <div>
                                            <span className="fw-semibold text-secondary">
                                              {serie.serie}
                                            </span>
                                          </div>
                                        </div>
                                      </td>
                                      <td>
                                        <span className="fw-semibold text-secondary">
                                          {formatCurrency(serie.totalOrcado)}
                                        </span>
                                      </td>
                                      <td>
                                        <span className="fw-semibold text-success">
                                          {formatCurrency(serie.totalRealizado)}
                                        </span>
                                      </td>
                                      <td>
                                        <span className="text-primary">
                                          {formatCurrency(serie.totalMulta)}
                                        </span>
                                      </td>
                                      <td>
                                        <span className="text-primary">
                                          {formatCurrency(serie.totalJuros)}
                                        </span>
                                      </td>
                                      <td>
                                        <span className="text-danger">
                                          {formatCurrency(serie.totaldesconto)}
                                        </span>
                                      </td>

                                      <td>
                                        <span className="fw-semibold text-dark">
                                          {formatCurrency(serie.totalLiquido)}
                                        </span>
                                      </td>
                                      <td>
                                        <div className="d-flex align-items-center gap-2">
                                          <div
                                            className="progress flex-grow-1"
                                            style={{
                                              height: "6px",
                                              width: "50px",
                                            }}
                                          >
                                            <div
                                              className={`progress-bar ${
                                                performanceSerie >= 90
                                                  ? "bg-success"
                                                  : performanceSerie >= 50
                                                  ? "bg-primary"
                                                  : "bg-danger"
                                              }`}
                                              style={{
                                                width: `${Math.min(
                                                  performanceSerie,
                                                  100
                                                )}%`,
                                              }}
                                            ></div>
                                          </div>
                                          <span
                                            className={`small ${
                                              performanceSerie >= 90
                                                ? "text-success"
                                                : performanceSerie >= 50
                                                ? "text-primary"
                                                : "text-danger"
                                            }`}
                                          >
                                            {performanceSerie.toFixed(1)}%
                                          </span>
                                        </div>
                                      </td>
                                    </tr>
                                  </React.Fragment>
                                );
                              })}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="min-vh-100 bg-light">
        {/* Header */}
        <header className="bg-white shadow-sm border-bottom">
          <div className="container-fluid">
            <div className="d-flex justify-content-between align-items-center py-3">
              <div className="d-flex align-items-center gap-3">
                <div className="p-2 bg-primary bg-opacity-10 rounded">
                  <BookOpen size={24} className="text-primary" />
                </div>
                <div>
                  <h4 className="mb-0 text-dark fw-bold">
                    Sistema Educacional - {localStorage.getItem("NomeFilial")}
                  </h4>
                  <small className="text-muted">
                    Gestão Financeira • Ano {localStorage.getItem("selectedYear")}
                    {!podeEditar() && (
                      <span className="badge bg-warning text-dark ms-2">
                        <Lock size={12} className="me-1" />
                        Somente Leitura
                      </span>
                    )}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Status */}
        {loadingMovimentos && (
          <div className="container-fluid mt-3">
            <div className="alert alert-primary d-flex align-items-center">
              <div className="spinner-border spinner-border-sm me-3"></div>
              Carregando dados do sistema...
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="container-fluid mt-3">
          <ul className="nav nav-pills bg-white rounded shadow-sm p-2">
            {[
              { id: "lancamento", nome: "Lançamentos", icone: Edit2 },
              { id: "conferencia", nome: "Conferência", icone: Eye },
              { id: "realizado", nome: "Realizado", icone: Target },
            ].map((aba) => (
              <li key={aba.id} className="nav-item">
                <button
                  onClick={() => setAbaAtiva(aba.id)}
                  className={`nav-link d-flex align-items-center gap-2 ${
                    abaAtiva === aba.id ? "active" : ""
                  }`}
                >
                  <aba.icone size={16} />
                  {aba.nome}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Conteúdo */}
        <div className="container-fluid mt-4 pb-5">
          {abaAtiva === "lancamento" && <AbaLancamento />}
          {abaAtiva === "conferencia" && <AbaConferencia />}
          {abaAtiva === "realizado" && <AbaRealizado />}
        </div>
      </div>

      <style jsx>{`
        .editable-field {
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 4px;
          transition: background-color 0.2s ease;
          display: inline-flex;
          align-items: center;
        }

        .editable-field:hover {
          background-color: #f8f9fa;
        }

        .editable-field-disabled {
          cursor: not-allowed;
          opacity: 0.7;
        }

        .editable-field-disabled:hover {
          background-color: #f8f9fa;
        }

        .edit-icon {
          opacity: 0;
          margin-left: 4px;
          transition: opacity 0.2s ease;
        }

        .lock-icon {
          opacity: 0.5;
          margin-left: 4px;
        }

        .editable-field:hover .edit-icon {
          opacity: 0.6;
        }

        .editable-field-disabled:hover .lock-icon {
          opacity: 0.8;
        }

        .spinning {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .table-hover tbody tr:hover {
          background-color: rgba(13, 110, 253, 0.04);
        }

        .card {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        }

        .progress {
          background-color: #f8f9fa;
        }

        .nav-pills .nav-link {
          color: #6c757d;
          font-weight: 500;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .nav-pills .nav-link:hover {
          background-color: #f8f9fa;
          color: #495057;
        }

        .nav-pills .nav-link.active {
          background-color: #0d6efd;
          color: white;
        }

        .badge {
          font-size: 0.875rem;
        }

        .table-primary {
          background-color: rgba(13, 110, 253, 0.08) !important;
        }

        .table-secondary {
          background-color: rgba(108, 117, 125, 0.08) !important;
        }

        .btn-link {
          color: #6c757d;
          text-decoration: none;
        }

        .btn-link:hover {
          color: #495057;
        }
      `}</style>
    </>
  );
};

export default SistemaEducacional;